import * as Sentry from "@sentry/react";

/**
 * Initialize Sentry error tracking and performance monitoring
 *
 * This should be called once at application startup, before React mounts.
 * Configuration is driven by environment variables.
 */
export const initializeSentry = () => {
  // Only initialize if DSN is provided
  if (!process.env.REACT_APP_SENTRY_DSN) {
    console.warn("Sentry DSN not configured. Skipping initialization.");
    return;
  }

  try {
    Sentry.init({
      dsn: process.env.REACT_APP_SENTRY_DSN,
      environment: process.env.REACT_APP_SENTRY_ENVIRONMENT || "development",

      // Performance monitoring - trace a percentage of transactions
      tracesSampleRate: parseFloat(
        process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE || "0.1"
      ),

      // PII data - automatically capture IP addresses and user agent
      sendDefaultPii: process.env.REACT_APP_SENTRY_SEND_DEFAULT_PII === "true",

      // React-specific integrations
      integrations: [
        // Automatic instrumentation for browser performance
        Sentry.browserTracingIntegration(),

        // Session replay for debugging errors
        Sentry.replayIntegration({
          // Mask all text and input fields by default for privacy
          maskAllText: true,
          blockAllMedia: true,
        }),
      ],

      // Session replay configuration
      replaysSessionSampleRate: 0.1, // 10% of normal sessions
      replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors

      // Filter out common noise that doesn't indicate real problems
      ignoreErrors: [
        // Browser resize observer errors (benign)
        "ResizeObserver loop limit exceeded",
        "ResizeObserver loop completed with undelivered notifications",

        // Promise rejections without proper error objects
        "Non-Error promise rejection captured",

        // Network errors that are often transient
        "NetworkError",
        "Network request failed",

        // Common browser extension errors
        "top.GLOBALS",
        "originalCreateNotification",
        "canvas.contentDocument",
        "MyApp_RemoveAllHighlights",
        "atomicFindClose",

        // Safari-specific errors
        "Can't find variable: ZiteReader",
        "jigsaw is not defined",
        "ComboSearch is not defined",

        // Chrome extension errors
        "Blocked a frame with origin",
      ],

      // Hook to modify events before sending (optional)
      beforeSend(event, hint) {
        // Filter out errors from browser extensions
        if (event.exception) {
          const error = hint.originalException;
          if (error && error.message && error.message.includes("extension://")) {
            return null; // Don't send to Sentry
          }
        }

        return event;
      },
    });

    console.log(
      `[Sentry] Initialized in ${process.env.REACT_APP_SENTRY_ENVIRONMENT || "development"} mode`
    );
  } catch (error) {
    console.error("[Sentry] Failed to initialize:", error);
  }
};

// Re-export Sentry for use in error boundaries and manual error tracking
export { Sentry };
